import { useEffect } from "react";

/**
 * One delegated pointer listener for the redesign's interaction primitives.
 * Elements opt in with data attributes; the CSS lives in src/styles/calm.css.
 *
 *   data-spotlight   → writes --mx / --my (px) so a radial glow follows the cursor
 *   data-tilt="8"    → writes --rx / --ry (deg) for a subtle 3D tilt (max deg)
 *   data-magnetic="10" → writes --tx / --ty (px), pulling the element toward the cursor
 *
 * Only runs for fine pointers and when reduced motion is not requested.
 */
export function InteractiveFX() {
  useEffect(() => {
    if (typeof window === "undefined") return;
    const fine = window.matchMedia("(hover: hover) and (pointer: fine)");
    const reduced = window.matchMedia("(prefers-reduced-motion: reduce)");
    if (!fine.matches) return;

    let frame = 0;
    let lastEvent: PointerEvent | null = null;
    let tiltEl: HTMLElement | null = null;
    let magnetEl: HTMLElement | null = null;

    const resetTilt = (el: HTMLElement) => {
      el.style.setProperty("--rx", "0deg");
      el.style.setProperty("--ry", "0deg");
      el.removeAttribute("data-tilting");
    };
    const resetMagnet = (el: HTMLElement) => {
      el.style.setProperty("--tx", "0px");
      el.style.setProperty("--ty", "0px");
      el.removeAttribute("data-magnet-active");
    };

    const run = () => {
      frame = 0;
      const e = lastEvent;
      if (!e) return;
      const target = e.target instanceof Element ? e.target : null;

      // Spotlight — every ancestor that opted in gets coordinates
      let node = target?.closest<HTMLElement>("[data-spotlight]") ?? null;
      while (node) {
        const r = node.getBoundingClientRect();
        node.style.setProperty("--mx", `${e.clientX - r.left}px`);
        node.style.setProperty("--my", `${e.clientY - r.top}px`);
        node = node.parentElement?.closest<HTMLElement>("[data-spotlight]") ?? null;
      }

      if (reduced.matches) return;

      // Tilt
      const nextTilt = target?.closest<HTMLElement>("[data-tilt]") ?? null;
      if (tiltEl && tiltEl !== nextTilt) resetTilt(tiltEl);
      tiltEl = nextTilt;
      if (tiltEl) {
        const max = Number(tiltEl.dataset.tilt) || 6;
        const r = tiltEl.getBoundingClientRect();
        const px = (e.clientX - r.left) / r.width - 0.5;
        const py = (e.clientY - r.top) / r.height - 0.5;
        tiltEl.style.setProperty("--rx", `${(-py * max).toFixed(2)}deg`);
        tiltEl.style.setProperty("--ry", `${(px * max).toFixed(2)}deg`);
        tiltEl.setAttribute("data-tilting", "true");
      }

      // Magnetic
      const nextMagnet = target?.closest<HTMLElement>("[data-magnetic]") ?? null;
      if (magnetEl && magnetEl !== nextMagnet) resetMagnet(magnetEl);
      magnetEl = nextMagnet;
      if (magnetEl) {
        const strength = Number(magnetEl.dataset.magnetic) || 8;
        const r = magnetEl.getBoundingClientRect();
        const dx = (e.clientX - (r.left + r.width / 2)) / (r.width / 2);
        const dy = (e.clientY - (r.top + r.height / 2)) / (r.height / 2);
        magnetEl.style.setProperty("--tx", `${(dx * strength).toFixed(2)}px`);
        magnetEl.style.setProperty("--ty", `${(dy * strength * 0.6).toFixed(2)}px`);
        magnetEl.setAttribute("data-magnet-active", "true");
      }
    };

    const onMove = (e: PointerEvent) => {
      if (e.pointerType !== "mouse") return;
      lastEvent = e;
      if (!frame) frame = requestAnimationFrame(run);
    };
    const onLeave = () => {
      if (tiltEl) resetTilt(tiltEl);
      if (magnetEl) resetMagnet(magnetEl);
      tiltEl = magnetEl = null;
    };

    window.addEventListener("pointermove", onMove, { passive: true });
    document.documentElement.addEventListener("pointerleave", onLeave);
    return () => {
      if (frame) cancelAnimationFrame(frame);
      window.removeEventListener("pointermove", onMove);
      document.documentElement.removeEventListener("pointerleave", onLeave);
    };
  }, []);

  return null;
}
